import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { dataTypes } from '@adalo/constants'
import { useFlags } from 'flags'

import { getUpgraded, getTrialInformation } from 'ducks/apps'
import Button from 'components/Shared/Button'
import Icon from 'components/Shared/Icon'
import EmptyState from 'components/Shared/EmptyState'
import IntegrationPackTrial from 'components/Shared/IntegrationPackTrial'
import FreeTrial from 'components/Shared/FreeTrial'

import AddCollection from './AddCollection'
import Item from './Item'

const defaultFields = [
  {
    type: dataTypes.TEXT,
    name: 'Name',
    isPrimaryField: true,
  },
]

const Items = props => {
  const {
    appId,
    createTable,
    datasources,
    datasourceId,
    metrics,
    removeTable,
    updateTable,
  } = props

  const { hasNewFreeTrial } = useFlags()
  const datasource = datasources.length === 1 && datasources[0]

  const [createTableId, setCreateTableId] = useState('')
  const [isAddingCollection, setIsAddingCollection] = useState(false)

  const toggleAddCollection = () => {
    setIsAddingCollection(!isAddingCollection)
  }

  const { showPaidFeatures, isAfterTrial } = useSelector(state => {
    const { trialState } = getTrialInformation(state, appId)
    const paying = getUpgraded(state, appId)
    const isAfterTrial = !paying && trialState === 'after'

    const showPaidFeatures =
      paying || trialState === 'during' || trialState === 'after'

    return { showPaidFeatures, isAfterTrial }
  })

  if (!datasource) return null
  if (!datasource.tables) return null

  // convert tables object into an array of tables
  const tables = Object.keys(datasource.tables).map(i => {
    // convert table key to tabledId property in object
    datasource.tables[i].tableId = i

    return datasource.tables[i]
  })

  // sort the tables where 'Users' is first, with everything else following
  const normalTables = [
    tables.find(i => i.name === 'Users'),
    ...tables.filter(i => i.name !== 'Users' && i.type !== 'api'),
  ]

  const apiTables = tables.filter(i => i.type === 'api')

  const handleAddCollection = values => {
    const { id } = createTable(
      appId,
      datasourceId,
      String(values.name).trim(),
      null,
      defaultFields
    )

    return setCreateTableId(id)
  }

  const renderAddCollection = () => {
    if (!isAddingCollection) {
      return (
        <div className="add-collection-wrapper">
          <Button
            component="div"
            orange
            onClick={toggleAddCollection}
            listAddButton
          >
            <Icon type="plus" /> Add Collection
          </Button>
        </div>
      )
    }

    const collectionProps = {
      createTableId,
      onSubmit: handleAddCollection,
      isAddingCollection,
      toggle: toggleAddCollection,
    }

    return <AddCollection {...collectionProps} />
  }

  const addApiUrl = `/apps/${appId}/data/new-external-collection`

  return (
    <>
      <div>
        <p className="data-collections-section-title">Database Collections</p>
        {normalTables.map(table => {
          if (!table) return null
          const metric = metrics[table.tableId]

          return (
            <Item
              key={table.tableId}
              appId={appId}
              datasourceId={datasourceId}
              metric={metric}
              removeTable={removeTable}
              updateTable={updateTable}
              {...table}
            />
          )
        })}
        {renderAddCollection()}
      </div>
      <div>
        <p className="data-collections-section-title">External Collections</p>
        {showPaidFeatures && (
          <>
            {apiTables.map(table => {
              if (!table) return null

              return (
                <Item
                  key={table.tableId}
                  appId={appId}
                  datasourceId={datasourceId}
                  removeTable={removeTable}
                  updateTable={updateTable}
                  {...table}
                />
              )
            })}
            {apiTables.length === 0 && !isAfterTrial && (
              <EmptyState bordered className="data-api-collection-empty-state">
                <h2>Connect to APIs</h2>
                <p>
                  Make your app even more powerful
                  <br />
                  by connecting to 3rd party APIs
                </p>
              </EmptyState>
            )}
            {!isAfterTrial && (
              <div className="add-collection-wrapper">
                <Button
                  orange
                  listAddButton
                  to={addApiUrl}
                  className="add-collection-button"
                >
                  <Icon type="plus" /> Add Collection
                </Button>
              </div>
            )}
          </>
        )}

        {hasNewFreeTrial ? (
          <FreeTrial appId={appId} type="externalCollection" />
        ) : (
          <IntegrationPackTrial appId={appId} type="externalCollection" />
        )}
      </div>
    </>
  )
}

export default Items
